import { clsx } from 'clsx'
import { forwardRef, useLayoutEffect, useRef, useMemo } from 'react'

type CardFaceProps = {
  title: string
}

const CardFace = forwardRef<HTMLDivElement, CardFaceProps>(function CardFace(
  { title: titleProp },
  ref,
) {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLHeadingElement>(null)

  const [title, subtitle] = useMemo(() => {
    const array = titleProp.split(' — ')
    return [array[0], array[1]]
  }, [titleProp])

  useLayoutEffect(() => {
    const titleElement = titleRef.current
    const subtitleElement = subtitleRef.current
    if (!titleElement) return

    titleElement.style.removeProperty('font-size')
    const maxFontSize = Number.parseFloat(window.getComputedStyle(titleElement).fontSize)
    const minFontSize = 12

    const fitTitle = () => {
      let fontSize = maxFontSize
      titleElement.style.fontSize = `${fontSize}px`
      if (subtitleElement) {
        subtitleElement.style.fontSize = `${fontSize - 2}px`
      }

      while (
        fontSize > minFontSize &&
        (titleElement.scrollHeight > titleElement.clientHeight ||
          titleElement.scrollWidth > titleElement.clientWidth)
      ) {
        fontSize -= 1
        titleElement.style.fontSize = `${fontSize}px`
        if (subtitleElement) {
          subtitleElement.style.fontSize = `${fontSize - 2}px`
        }
      }
    }

    const resizeObserver = new ResizeObserver(fitTitle)
    resizeObserver.observe(titleElement.parentElement ?? titleElement)
    fitTitle()

    return () => resizeObserver.disconnect()
  }, [title])

  return (
    <div ref={ref} className="choice-card-face choice-card-face-bg choice-card-face--front">
      <div
        className={clsx(
          'choice-card-face-inner card-face-font flex min-h-0',
          'grow flex-col items-center justify-center gap-6 p-6 text-center italic',
        )}
      >
        <Ornament />
        <h3
          ref={titleRef}
          className="min-h-0 w-full overflow-hidden text-3xl font-bold wrap-break-word"
        >
          {title}
        </h3>
        {subtitle ? (
          <h4
            ref={subtitleRef}
            className="card-face-font-subtitle min-h-0 w-full overflow-hidden text-xl font-semibold wrap-break-word"
          >
            {subtitle}
          </h4>
        ) : (
          <div />
        )}
        <Ornament />
      </div>
    </div>
  )
})

export default CardFace

function Ornament() {
  return (
    <div className="choice-card-ornament-row">
      <svg
        width="52"
        height="12"
        viewBox="0 0 52 12"
        fill="currentColor"
        aria-hidden="true"
        className="choice-card-face-ornament-svg"
      >
        <polygon points="6,1 11,6 6,11 1,6" />
        <polygon points="26,1 31,6 26,11 21,6" />
        <polygon points="46,1 51,6 46,11 41,6" />
      </svg>
    </div>
  )
}
